import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { translateKey } from 'src/locales/translate';
import WarehouseBadge from 'src/sections/@dashboard/warehouse/components/WarehouseBadge';
import { formatProductStock } from 'src/sections/@dashboard/warehouse/utils/stockDisplay';
import MedicinePreparationChip from './MedicinePreparationChip';
import {
  WarehouseMedicineOption,
  formatExpiryMonth,
  optionStockColor,
} from './medicineSearchOptions';

interface WarehouseMedicineOptionContentProps {
  option: WarehouseMedicineOption;
}

/** A drug Product of the Facility: its name and flags, then its stock at a glance (#690). */
export default function WarehouseMedicineOptionContent({
  option,
}: WarehouseMedicineOptionContentProps): React.ReactElement {
  const { product, medicine, nextExpiryDate } = option;
  return (
    <Box sx={{ width: '100%' }} data-testid="medicine-option-warehouse">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box sx={{ fontWeight: 500, flex: 1 }}>{product.name}</Box>
        {medicine?.preparationType && (
          <MedicinePreparationChip preparationType={medicine.preparationType} />
        )}
        {product.isRx && (
          <WarehouseBadge
            label={translateKey('warehouse.products.badges.rx')}
            hint={translateKey('warehouse.products.badges.rxHint')}
            color="info"
            icon="solar:document-medicine-bold"
          />
        )}
        {product.controlledCategory && (
          <WarehouseBadge
            label={translateKey('warehouse.medicineSearch.controlled')}
            hint={translateKey('warehouse.products.badges.controlledHint', {
              category: product.controlledCategory,
            })}
            color="warning"
            icon="solar:shield-warning-bold"
          />
        )}
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 1.5 }}>
        <Typography variant="caption" color={optionStockColor(product)}>
          {translateKey('warehouse.medicineSearch.stock', {
            quantity: formatProductStock(product.onHandMilli, product),
          })}
        </Typography>
        {nextExpiryDate && (
          <Typography variant="caption" color="text.secondary">
            {translateKey('warehouse.medicineSearch.expiry', {
              date: formatExpiryMonth(nextExpiryDate),
            })}
          </Typography>
        )}
        {product.isBelowMinimum && (
          <Typography variant="caption" color="warning.main">
            {translateKey('warehouse.medicineSearch.belowMinimum')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
